import React, { useState, useEffect, useRef } from 'react';
import PageNavi from '../PageNavi';
import TextFilter from '../Filters/TextFilter';
import DropdownFilter from '../Filters/DropdownFilter';
import ResultsOnPage from '../ResultsOnPage';
import ExportButton from '../ExportButton';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, ConstrainMode, IconButton, Stack, Spinner, SpinnerSize, Link, ColumnActionsMode, Label, Icon, TooltipHost } from '@fluentui/react';
import Api from '../../../services/Api';
import axios from 'axios';
import { createAndDownloadCsv, getGroupedItems, mapBooleanToFilterValues, mapEnumToFilterValues, renderBoolean, renderDate, renderManagement, renderUser } from '../../../helpers/HelperMethods';
import { PowerAppEnvironmentType } from '../../../GlobalConst/globalEnums';
import DropdownTree from '../../CustomComponents/DropdownTree';
import EnvironmentActionButtonsView from './EnvironmentActionButtonsView';

const CancelToken = axios.CancelToken;

const EnvironmentsList = ({defaultEnvironments, filterBy}) => {

    const apiAddress = '/EnvironmentsPanel';

    const [isLoading, setIsLoading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [environments, setEnvironments] = useState([]);
    const [orderBy, setOrderBy] = useState({ fieldName: '', isDescending: false});
    const [totalResults, setTotalResults] = useState(0);
    const [onPage, setOnPage] = useState(20);
    const [pageUrlFilter, setPageUrlFilter] = useState(1);
    const [filters, setFilters] = useState({});
    const cancelSource = useRef(null);
    const firstUpdate = useRef(true);
    const [allTotalResults, setAllTotalResults] = useState(0)

    useEffect(() => {
        if (defaultEnvironments && firstUpdate.current) {
            firstUpdate.current = false;
            setEnvironments(defaultEnvironments.results);
            setTotalResults(defaultEnvironments.totalCount);
            setAllTotalResults(defaultEnvironments.allTotalCount);
        }
        else {
            return getEnvironments()
        }
    }, [onPage, pageUrlFilter, filters, orderBy])

    const _onColumnClick = (ev, column) => {
        setOrderBy({
            fieldName: column.fieldName, 
            isDescending: !column.isSortedDescending
        });
    }

    const getIcons = (environment) =>
        <div style={{marginRight: 10, display: 'flex', gap: 10, cursor: 'pointer'}}>
            {
                environment.isIcofr && 
                <TooltipHost content='ICoFR Environment'>
                    <Icon iconName='AllCurrency' styles={{root: {fontSize: 18}}} />
                </TooltipHost> 
            }
            {
                environment.isManaged && 
                <TooltipHost content='Managed Environment'>
                    <Icon iconName='ProductionFloorManagement' styles={{root: {fontSize: 20}}} />
                </TooltipHost>
            }
            {
                // environment.isStrictlyConfidential && 
                // <TooltipHost content='Strictly Confidential Environment'>
                //     <Icon iconName='Permissions' />
                // </TooltipHost>
            }
        </div>

    const initialColumns: IColumn[] = [
        {
            key: 'name',
            name: 'Name / ID',
            fieldName: 'name',
            minWidth: 150,
            maxWidth: 400,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => 
                <div style={{display: 'flex'}}>
                    {getIcons(item)}
                    <Link href={item.url} target='_blank'>{item.name}</Link>
                </div>
        },
        {
            key: 'owner',
            name: 'Owner',
            fieldName: 'owner',
            minWidth: 160,
            maxWidth: 300,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.owner)
        },
        {
            key: 'management',
            name: 'Business Area',
            fieldName: 'management',
            minWidth: 50,
            maxWidth: 160,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderManagement(item.management)
        },
        {
            key: 'type',
            name: 'Type',
            fieldName: 'type',
            minWidth: 150,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
        },
        {
            key: 'securityGroup',
            name: 'Security Group',
            fieldName: 'securityGroup',
            minWidth: 160,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderUser(item.securityGroup)
        },
        {
            key: 'isManaged',
            name: 'Is Managed',
            fieldName: 'isManaged',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderBoolean(item.isManaged)
        },
        {
            key: 'isIcofr',
            name: 'Is ICoFR',
            fieldName: 'isIcofr',
            minWidth: 50,
            maxWidth: 100,
            isRowHeader: false,
            isResizable: true,
            data: 'string',
            onRender: (item) => renderBoolean(item.isIcofr)
        },
        // {
        //     key: 'lastRecertificationDate',
        //     name: 'Last Recertification Date',
        //     fieldName: 'lastRecertificationDate',
        //     minWidth: 160,
        //     maxWidth: 200,
        //     isRowHeader: false,
        //     isResizable: true,
        //     data: 'number',
        //     onRender: (item) => renderDate(item.lastRecertificationDate)
        // },
        {
            key: 'created',
            name: 'Created on',
            fieldName: 'created',
            minWidth: 150,
            maxWidth: 200,
            isRowHeader: false,
            isResizable: true,
            data: 'number',
            onRender: (item) => renderDate(item.created)
        },
        {
            key: 'edit',
            name: 'Actions',
            minWidth: 64,
            maxWidth: 64,
            isResizable: false,
            isIconOnly: true,
            columnActionsMode: ColumnActionsMode.disabled,
            onRender: (item) => <EnvironmentActionButtonsView editedItem={item}  />
        }
    ] 

    const getColumns = (initialColumns) => {
 
        const columns = initialColumns.map(c => {
            c.isFiltered = (filters[c.fieldName] || '').length > 0;
            c.isSorted = orderBy.fieldName === c.fieldName;
            c.isSortedDescending = c.isSorted ? orderBy.isDescending : true;
            return c;
        });

        return columns;
    }

    const onFilterChange = (fieldName, value) => {
        setFilters({
            ...filters,
            [fieldName]: value
        });
        setPageUrlFilter(1);
    }

    const loadData = (envs) => {
        setEnvironments(envs);
        setIsLoading(false);
    }

    const handleNoSelection = () => {
        loadData([]);
        setTotalResults(0);
        setAllTotalResults(0)
    }

    const getResultsUrl = (isExport) => {
        let url = `${apiAddress}?`
        
        if (isExport)
            url += `forExport=1`;
        else
            url += `page=${pageUrlFilter}&onPage=${onPage}`;

        const filterUrl = Object.keys(filters).reduce((queryString, filterField) => {
            const filterText = filters[filterField];
            if(filterText) {
                queryString += `&${filterField}=${encodeURIComponent(filterText)}`;
            }
            return queryString;
        }, '');

        let orderByUrl = '';
        if(orderBy.fieldName) {
            orderByUrl = `&orderBy=${orderBy.fieldName}&isDesc=${orderBy.isDescending}`;
        }

        url = url + filterUrl + orderByUrl;

        if (filterBy) {
            url += `&filterBy=${filterBy}`
        }

        return url
    }

    const getEnvironments = () => {
        setIsLoading(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(false), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            loadData(posts.results);
            setTotalResults(posts.totalCount);
            setAllTotalResults(posts.allTotalCount)
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }
    }

    const exportFields = [
        {
            label: 'Name',
            value: 'name'
        },{
            label: 'Owner',
            value: 'owner.email'
        },{
            label: 'Business Area',
            value: 'management.key'
        },{
            label: 'Type',
            value: 'type'
        },{
            label: 'Security Group',
            value: 'securityGroup.email'
        },{
            label: 'Is Managed',
            value: 'isManaged'
        },{
            label: 'Is ICoFR',
            value: 'isIcofr'
        },{
            label: 'Created on',
            value: 'created'
        },
    ]

    const exportData = () => {
        setIsExporting(true);

        if (cancelSource.current) {
            cancelSource.current.cancel();
        }

        cancelSource.current = CancelToken.source();

        Api.get(getResultsUrl(true), {
            cancelToken: cancelSource.current.token
        })
        .then(res => res.data)
        .then(posts => {
            createAndDownloadCsv(posts, "environmentsDetails", exportFields);
            setIsExporting(false);
        })
        .catch(err => {});

        return () => {
            cancelSource.current.cancel();
        }     
    }

    const columns = getColumns(initialColumns);
    const spinner = <Spinner style={{margin: '200px'}} size={SpinnerSize.large} />;
    
    const [sortedItems, groups] = getGroupedItems(environments, 'prodEnvironmentId', 'prodEnvironmentName');

    const groupedPagingInfo = allTotalResults > 0 &&
        <Label>
            <div>Showing environment groups with pagination.</div>
            <div>Total environments across all groups: {allTotalResults}.</div>
        </Label>

    return( 
        <div>
            <div>
                    <div className="filters">
                        <div className="cell">
                            <TextFilter
                                fieldName='name'
                                fieldLabel='Name'
                                onChange={onFilterChange}
                            />
                        </div>  
                        <div className="cell">
                            <TextFilter
                                fieldName='owner'
                                fieldLabel='Owner'
                                onChange={onFilterChange}
                            />
                        </div>   
                        <div className="cell">
                            <DropdownTree
                                address={'/Management?parent='}
                                fieldName='organization'
                                label='Busines Area'
                                multiselect={false}
                                onChange={onFilterChange}
                            />
                        </div>           
                        <div className="cell">
                            <DropdownFilter
                                fieldName="type"
                                title='Environment Type'
                                onChange={onFilterChange}
                                noselection={handleNoSelection}
                                includeSelectAll={true}
                                values={mapEnumToFilterValues(PowerAppEnvironmentType).filter(x => x.value !== PowerAppEnvironmentType.teams)}
                            />
                        </div>       
                        <div className="cell">
                            <TextFilter
                                fieldName='securityGroup'
                                fieldLabel='Security Group'
                                onChange={onFilterChange}
                            />
                        </div>     
                        <div className="cell">
                            <DropdownFilter
                                fieldName="isManaged"
                                title='Is Managed'
                                onChange={onFilterChange}
                                noselection={handleNoSelection}
                                values={mapBooleanToFilterValues()}
                            />
                        </div>       
                        <div className="cell">
                            <DropdownFilter
                                fieldName="isIcofr"
                                title='Is ICoFR'
                                onChange={onFilterChange}
                                noselection={handleNoSelection}
                                values={mapBooleanToFilterValues()}
                            />
                        </div> 
                    </div>
                        <div style={{position:'relative', margin: '20px 0'}}>
                            <div>
                                <IconButton 
                                    style={{color: 'rgb(96, 94, 92)', position: 'absolute'}} 
                                    onClick={getEnvironments}
                                    iconProps={{iconName: 'refresh'}} 
                                />
                                <PageNavi
                                    pageNumPart = {setPageUrlFilter}
                                    totalResults = {totalResults}
                                    resultsOnPage = {onPage}
                                    pageNum = {pageUrlFilter}
                                    showTotalResults = {true}
                                    additionalInfo={groupedPagingInfo}
                                />
                                <Stack horizontal disableShrink>
                                    <ResultsOnPage
                                        onPage = {onPage}
                                        onPageResults = {setOnPage}
                                    />
                                    <ExportButton 
                                        onClick = {exportData}
                                        loading = {isExporting}
                                    />
                                </Stack>                            
                            </div>
                        </div>
            </div>
            <div>
                {
                    isLoading ?
                    spinner 
                    : <DetailsList
                        items={sortedItems}
                        columns={columns}
                        layoutMode={DetailsListLayoutMode.justified}
                        isHeaderVisible={true}
                        selectionMode={SelectionMode.none}
                        styles={{root: {width: '100%'}}}
                        onColumnHeaderClick={_onColumnClick}
                        constrainMode={ConstrainMode.unconstrained}
                        groups={groups}
                    />
                }
            </div>
        </div>
    )
}

export default EnvironmentsList;


// WEBPACK FOOTER //
// ./src/components/Administration/PowerPlatform/EnvironmentsList.js